import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRoles } from "redux/actions/Master";
import { AppState } from "redux/store";
import { UserData } from "types/models/master/MasterApp";
import AppTableContainer from "@crema/core/AppTableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableHeading from "./TableHeading";
import TableItem from "./TableItem";

type Order = "asc" | "desc";

interface UserTableProps {
  users: UserData[];
}

const UserTable: React.FC<UserTableProps> = ({ users }) => {
  const dispatch = useDispatch();
  const roles = useSelector((state: AppState) => state.master.roles);

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof UserData | "roleName">("username");

  useEffect(() => {
    dispatch(getRoles());
  }, [dispatch]);

  const getRoleName = (roleId: number) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.role : "N/A";
  };

  const handleSort = (property: keyof UserData | "roleName") => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const descendingComparator = (
    a: UserData & { roleName?: string },
    b: UserData & { roleName?: string },
    orderBy: keyof UserData | "roleName"
  ) => {
    const valA = orderBy === "roleName" ? a.roleName : a[orderBy];
    const valB = orderBy === "roleName" ? b.roleName : b[orderBy];

    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;

    if (typeof valA === "string" && typeof valB === "string") {
      return valB.localeCompare(valA);
    }
    if (valA < valB) {
      return -1;
    }
    if (valA > valB) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order: Order, orderBy: keyof UserData | "roleName") => {
    return order === "desc"
      ? (a: UserData & { roleName?: string }, b: UserData & { roleName?: string }) =>
          descendingComparator(a, b, orderBy)
      : (a: UserData & { roleName?: string }, b: UserData & { roleName?: string }) =>
          -descendingComparator(a, b, orderBy);
  };

  const stableSort = (
    array: (UserData & { roleName?: string })[],
    comparator: (a: UserData, b: UserData) => number
  ) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [UserData & { roleName?: string }, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const usersWithRoleName = users.map((user) => ({
    ...user,
    roleName: getRoleName(user.roleId),
  }));

  const sortedUsers = stableSort(usersWithRoleName, getComparator(order, orderBy));

  return (
    <AppTableContainer>
      <Table stickyHeader className="table">
        <TableHead>
          <TableHeading order={order} orderBy={orderBy} onSort={handleSort} />
        </TableHead>
        <TableBody>
          {sortedUsers.map((user) => (
            <TableItem key={user.id} data={user} roleName={user.roleName || "N/A"} />
          ))}
        </TableBody>
      </Table>
    </AppTableContainer>
  );
};

export default UserTable;
